import React from "react";

import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";

import { BulletLegends } from "app/components/React/Legends/BulletLegends";
import { Legends } from "app/components/React/Legends/Legends";
import { EGridDirections } from "app/components/React/Legends/legendsTypes";
import { DefaultFetchService } from "app/services/fetchService";

import { OVERVIEW_AVERAGES_ENDPOINT } from "../UtilityFunctionsAndConstants/UtilityFunctions";

const LegendsWrapper = styled.div`
    padding-right: 10px;
`;

const LegendWrapperWithSeparator = styled.div`
    padding-top: 8px;
    width: 100%;
    border-top: 1px solid ${colorsPalettes.carbon[100]};
`;

const HeaderDataEndpoint = "api/WebsiteOverview/getheader";
const PublicGa = "Public";
const EndpointToLegendsDataName = {
    Visits: "AvgMonthVisits",
    UniqueUsers: "UniqueUsers",
    EngagementDedup: "DedupUniqueUsers",
    Duration: "AvgVisitDuration",
    PagesPerVisit: "PagesPerVisit",
    BounceRate: "BounceRate",
    PageViews: "TotalPagesViews",
};
export const LegendsContainer = ({
    metric,
    data,
    showGAApprovedData,
    sites,
    chartType,
    webSource,
    onLegendClick,
    queryParams,
    meta,
    showCategoryLegend,
}) => {
    const [state, setState] = React.useState({ legendsData: undefined, isGaPublicDict: {} });
    const { legendsData, isGaPublicDict } = state;
    const [isLoading, setIsLoading] = React.useState(true);
    const { isPOP, isSingleMode, isSingleCompare } = meta;
    const shouldGetLegendsData = !(isPOP || isSingleMode || isSingleCompare);
    const getData = () => {
        if (!shouldGetLegendsData) {
            setState({ legendsData: undefined, isGaPublicDict: undefined });
            setIsLoading(false);
            return;
        }
        const fetchService = DefaultFetchService.getInstance();
        const apiParams = { ...queryParams };
        const legendsDataPromise = fetchService.get(OVERVIEW_AVERAGES_ENDPOINT, apiParams);
        const headerDataPromise = fetchService.get(HeaderDataEndpoint, {
            includeCrossData: true,
            keys: showCategoryLegend ? apiParams.keys.split(",").slice(0, -1).join(",") : apiParams.keys,
        });
        Promise.allSettled([legendsDataPromise, headerDataPromise])
            .then((data: any) => {
                const dict = {};
                if (data[1].status === "fulfilled") {
                    sites.map(({ name, isCategory }) => {
                        if (isCategory) {
                            return;
                        }
                        dict[name] = data[1].value?.[name]?.privacyStatus === PublicGa;
                    });
                }
                setState({ legendsData: data[0].value?.Data, isGaPublicDict: dict });
            })
            .catch((e) => Sentry.error("Error fetching data for Traffic and engagement graph legends ", e))
            .finally(() => setIsLoading(false));
    };
    React.useEffect(getData, [queryParams.ShouldGetVerifiedData]);
    if (isLoading) {
        return "";
    }
    const getSiteAverage = (site) => {
        if (!legendsData || !shouldGetLegendsData) {
            return undefined;
        }
        const { name, alias, isCategory } = site;
        const metricName = metric.endpointMetric ?? metric.endpoint?.split("/")[2];
        let siteName = name;
        if (isCategory) {
            siteName = alias;
        }
        const siteData = (legendsData as any[]).find(({ Domain }) => Domain === siteName);
        return siteData?.[EndpointToLegendsDataName[metricName]];
    };
    const getIsGAVerified = (siteName) => {
        if (showGAApprovedData && data?.KeysDataVerification) {
            let site = siteName;
            if (isSingleCompare) {
                const legendObj = sites.find((st) => st.name === siteName);
                if (legendObj.isBeta) {
                    return false;
                }
                site = Object.keys(data.KeysDataVerification)?.[0];
            }
            return data.KeysDataVerification[site];
        }
        return false;
    };

    const getLegendItems = () =>
        sites.map((site, i, arr) => {
            const { name, isSelected, color, transparentColor, infoTooltip, isBeta, isCategory } = site;
            const addLineSeparatorBeforeItem = isCategory && showCategoryLegend && arr.length > 2;
            return {
                name,
                hidden: !isSelected,
                color,
                transparentColor,
                infoTooltip,
                isGAVerified: getIsGAVerified(name),
                isGAPrivate: isGaPublicDict ? !isGaPublicDict[name] : undefined,
                isBeta,
                data: metric.hideLegendsData ? "" : metric.yAxisLabelsFormatter({ value: getSiteAverage(site) }),
                ...(isCategory && { tooltip: name }),
                ...(addLineSeparatorBeforeItem && {
                    legendComponentWrapper: LegendWrapperWithSeparator,
                }),
            };
        });
    const defaultLegends =
        sites.length > 1 ? (
            <LegendsWrapper>
                <Legends
                    legendItems={getLegendItems()}
                    toggleSeries={onLegendClick}
                    showLegendsData={shouldGetLegendsData}
                    gridDirection={EGridDirections.COLUMN}
                    legendsGap="8px"
                    legendComponent={!isSingleMode && !isPOP ? LegendWithOneLineCheckboxFlex : null}
                    textMaxWidth={isSingleMode ? "200px" : "150px"}
                />
            </LegendsWrapper>
        ) : (
            <BulletLegends legendItems={getLegendItems()} />
        );
    const ChartLegends = metric.legends
        ? metric.legends({
              legendItems: getLegendItems(),
              toggleSeries: onLegendClick,
              webSource,
              chartType,
          })
        : defaultLegends;
    return ChartLegends;
};
